/**
 * API 调用封装
 */

import type { ApiResponse, ReceiverInfo, SendMessageParams } from "../types";
import { authFetch } from "./auth";

// API 基础路径（根据实际部署环境配置）
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";

/**
 * 根据 biz_key 前缀查询消息接收者列表
 * @param bizKeyPrefix biz_key 前缀（如：send_message_）
 * @returns 消息接收者列表
 */
export async function getReceiversByBizKeyPrefix(
  bizKeyPrefix: string
): Promise<ReceiverInfo[]> {
  // 调用后端真实接口
  try {
    // 确保bizKeyPrefix是字符串类型
    const prefix = String(bizKeyPrefix || "");

    const encodedPrefix = encodeURIComponent(prefix);

    // 修正API路径：list-like-biz-keys
    const url = `${API_BASE_URL}/message/receiver/list-like-biz-keys?page=1&pageSize=1000&bizKeys=${encodedPrefix}`;

    const response = await authFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse<any> = await response.json();

    if (result.code !== 1) {
      throw new Error(result.message || "获取消息接收者列表失败");
    }

    // 只关注 hrMessageReceiver 字段
    const receivers = result.data?.hrMessageReceiver || [];

    // 适配为 ReceiverInfo[]
    return receivers.map((item: any) => {
      return {
        id: item.receiverId,
        name: item.name,
        bizKeys: item.bizKeys || "[]", // 保留完整的 JSON 字符串数组
        groupName: item.groupName, //群名称
        atList: item.atList || "[]", // 群配置的负责人员工ID JSON 数组
      };
    });
  } catch (error) {
    console.error("获取消息接收者列表失败:", error);
    throw error;
  }
}

/**
 * 发送消息到群组
 * @param params 发送参数
 * @returns 返回消息ID
 */
export async function sendMessageToReceivers(
  params: SendMessageParams
): Promise<number> {
  try {
    const url = `${API_BASE_URL}/message/info/add`;

    const response = await authFetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse<any> = await response.json();

    if (result.code !== 1) {
      throw new Error(result.message || "发送消息失败");
    }

    const messageId = result.data?.hrMessageInfo?.messageId;

    if (!messageId) {
      console.error("❌ messageId为空，完整返回数据:", result);
      throw new Error("发送消息成功，但未返回messageId");
    }

    return messageId;
  } catch (error) {
    console.error("发送消息失败:", error);
    throw error;
  }
}

/**
 * 统计消息发送状态
 * @param messageIds 消息ID列表
 * @returns 统计结果
 */
export async function countMessageStatus(messageIds: number[]): Promise<{
  totalCount: number;
  sentCount: number;
  sendingCount: number;
  pendingCount: number;
}> {
  try {
    // 过滤掉无效的messageId
    const validIds = messageIds.filter(
      (id) => id !== undefined && id !== null && !isNaN(id)
    );

    if (validIds.length === 0) {
      return {
        totalCount: 0,
        sentCount: 0,
        sendingCount: 0,
        pendingCount: 0,
      };
    }

    // 构建查询参数：messageId[]=1&messageId[]=2&messageId[]=3
    const queryParams = validIds.map((id) => `messageId[]=${id}`).join("&");
    const url = `${API_BASE_URL}/message/info/count-by-message-id-list?${queryParams}`;

    const response = await authFetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const result: ApiResponse<any> = await response.json();

    if (result.code !== 1) {
      throw new Error(result.message || "统计消息状态失败");
    }

    return {
      totalCount: result.data?.totalCount || 0,
      sentCount: result.data?.sentCount || 0,
      sendingCount: result.data?.sendingCount || 0,
      pendingCount: result.data?.pendingCount || 0,
    };
  } catch (error) {
    console.error("统计消息状态失败:", error);
    throw error;
  }
}
